// ════════════════════════════════════════════════════════════════════════
// PrepSupportEngineerPage.tsx — Preparación: Application Support Engineer
// (.NET, Kubernetes & AWS)
// ════════════════════════════════════════════════════════════════════════
// Página dedicada (no genérica) para este documento de análisis, porque
// mezcla resumen de vacante + fortalezas/debilidades + diálogo + Q&A en
// una estructura única. Contenido en español — es un análisis estratégico
// sobre una vacante específica, no un guion de práctica genérico, así que
// aquí no se tradujo al inglés (a diferencia de los diálogos de
// dialogues.ts, que sí son bilingües por diseño).
// ════════════════════════════════════════════════════════════════════════

import { DashboardLayout } from '../layouts/DashboardLayout/DashboardLayout';
import { AppSidebarMenu } from '../components/AppSidebarMenu/AppSidebarMenu';
import interviewStyles from './InterviewPracticePage.module.css';
import styles from './PrepSupportEngineerPage.module.css';

export default function PrepSupportEngineerPage() {
  return (
    <DashboardLayout.Root>
      <DashboardLayout.Sidebar>
        <AppSidebarMenu />
      </DashboardLayout.Sidebar>

      <DashboardLayout.Content>
        <div className={interviewStyles.pageHeader}>
          <h1 className={interviewStyles.pageTitle}>
            Prep: Application Support Engineer (.NET, K8s, AWS)
          </h1>
          <p className={interviewStyles.pageSubtitle}>
            Análisis de la vacante, fortalezas y debilidades frente al CV, y simulación de entrevista.
          </p>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Resumen de la vacante</h2>
          <p className={styles.prose}>
            <strong>Ingeniero de Soporte de Aplicaciones (.NET, Kubernetes y AWS)</strong> — soporte
            técnico y mantenimiento continuo de aplicaciones empresariales de Participation Residuals
            (regalías de participación, típico de Media &amp; Entertainment), con foco en estabilidad
            de aplicaciones, resolución de incidentes en producción, despliegues y mejoras en un
            entorno cloud-native.
          </p>
          <p className={styles.prose}>
            Es una vacante de <strong>Ingeniero individual (IC), no de liderazgo</strong>, centrada en
            soporte de producción (monitoreo, incidentes, RCA, guardias) más desarrollo/mantenimiento
            .NET. Stack: .NET Framework/Core, Kubernetes + Docker, SQL Server/PostgreSQL/Oracle, AWS,
            Git/CI-CD. Buscan a alguien que resuelva problemas en producción todos los días, no que
            diseñe arquitecturas o lidere equipos — un cambio de rol frente a los últimos 5 años de
            Israel como Tech Lead Manager.
          </p>

          <div className={styles.columns}>
            <div className={styles.strengths}>
              <p className={styles.columnTitle}>Fortalezas</p>
              <ul className={styles.list}>
                <li><strong>.NET a fondo:</strong> 16 años, muy por encima del mínimo de 5 pedido.</li>
                <li><strong>AWS extenso:</strong> 8 años — EKS, ECS, Fargate, Lambda, RDS, CloudFormation, IAM.</li>
                <li><strong>Soporte de producción real:</strong> en NewYorkLife hizo exactamente lo que pide la vacante — monitoreo, RCA, guardias, ServiceNow con SLA. El punto más fuerte a destacar primero.</li>
                <li><strong>Bases de datos relacionales:</strong> PostgreSQL y Oracle a nivel experto, con tuning de queries.</li>
                <li><strong>Docker y contenedores:</strong> imágenes Linux para ECS/Fargate y Lambda — transferible a Kubernetes.</li>
                <li><strong>CI/CD y Git:</strong> Jenkins, CodePipeline, CodeBuild, GitHub Actions, Terraform.</li>
                <li><strong>Sector afín:</strong> Finanzas, Seguros, Bursátil, más un antecedente en TV Azteca (Media/Entretenimiento) que conecta con "Participation Residuals".</li>
              </ul>
            </div>

            <div className={styles.weaknesses}>
              <p className={styles.columnTitle}>Debilidades / puntos a preparar</p>
              <ul className={styles.list}>
                <li><strong>Kubernetes específico</strong> es el punto más débil — 3 años, frente al resto del stack mucho más profundo. Conviene tener ejemplos concretos o anclarlo en la fortaleza de contenedores/AWS en general.</li>
                <li><strong>SQL Server no aparece explícito</strong> en el CV — hay que tener lista una respuesta clara (Entity Framework normalmente corre sobre SQL Server).</li>
                <li><strong>Posible sobrecalificación:</strong> últimos 5+ años como Tech Lead Manager liderando 10-20+ personas. El reclutador probablemente preguntará por qué dar un paso atrás — hace falta una narrativa sólida.</li>
                <li><strong>Participation Residuals / M&amp;E:</strong> sin experiencia directa reciente en ese dominio de negocio — reconocerlo con seguridad, no evadirlo.</li>
                <li><strong>Riesgo de expectativa salarial:</strong> dado el nivel de seniority, vale la pena anticipar la pregunta.</li>
              </ul>
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <h2 className={interviewStyles.sectionTitle}>Diálogo de apertura</h2>
          <p className={interviewStyles.sectionNote}>Para generar interés al inicio de la reunión.</p>

          <div className={interviewStyles.script}>
            <div className={interviewStyles.turn}>
              <p className={`${interviewStyles.speaker} ${interviewStyles.speakerRecruiter}`}>Reclutador</p>
              <p className={interviewStyles.en}>Cuéntame un poco sobre ti y por qué te interesa esta posición.</p>
            </div>
            <div className={`${interviewStyles.turn} ${interviewStyles.turnCandidate}`}>
              <p className={`${interviewStyles.speaker} ${interviewStyles.speakerCandidate}`}>Israel</p>
              <p className={interviewStyles.en}>
                Con gusto. Tengo más de 18 años en Tecnologías de la Información, la mayoría de ellos
                trabajando con el stack .NET —desde Framework hasta .NET 6+— y en los últimos años
                sobre AWS a nivel muy profundo: EKS, ECS, Fargate, Lambda, RDS, todo el ecosistema. Lo
                que me atrae de esta vacante en particular es que combina justo lo que más disfruto
                hacer día a día: resolver problemas reales en producción. En mi paso por New York Life,
                además de diseñar la arquitectura de microservicios, yo mismo daba soporte directo a la
                aplicación —guardias de fin de semana, liberaciones nocturnas, análisis de causa raíz,
                tickets por ServiceNow con SLA— así que esta no es una descripción teórica para mí, es
                literalmente lo que hacía cada semana.
              </p>
            </div>
            <div className={interviewStyles.turn}>
              <p className={`${interviewStyles.speaker} ${interviewStyles.speakerRecruiter}`}>Reclutador</p>
              <p className={interviewStyles.en}>
                Veo que tus últimos roles han sido de liderazgo técnico, Tech Lead Manager. ¿Por qué te
                interesa una posición de soporte individual?
              </p>
            </div>
            <div className={`${interviewStyles.turn} ${interviewStyles.turnCandidate}`}>
              <p className={`${interviewStyles.speaker} ${interviewStyles.speakerCandidate}`}>Israel</p>
              <p className={interviewStyles.en}>
                Es una pregunta justa. Disfruto liderar equipos y seguiré haciéndolo cuando la
                oportunidad lo permita, pero en el fondo sigo siendo alguien que quiere estar cerca del
                código y de la operación —de hecho, en mis roles de liderazgo nunca dejé de tocar la
                parte técnica ni de meterme a resolver incidentes críticos yo mismo. Esta posición me
                permite enfocar ese tiempo directamente en la parte que más valor aporta: mantener la
                aplicación estable, optimizar el rendimiento y dar soporte confiable, sin la carga
                administrativa de gestionar personas. Además el stack —.NET, contenedores, AWS, bases
                de datos relacionales— es exactamente donde tengo más profundidad.
              </p>
            </div>
            <div className={interviewStyles.turn}>
              <p className={`${interviewStyles.speaker} ${interviewStyles.speakerRecruiter}`}>Reclutador</p>
              <p className={interviewStyles.en}>¿Qué sabes sobre Participation Residuals?</p>
            </div>
            <div className={`${interviewStyles.turn} ${interviewStyles.turnCandidate}`}>
              <p className={`${interviewStyles.speaker} ${interviewStyles.speakerCandidate}`}>Israel</p>
              <p className={interviewStyles.en}>
                Entiendo que se trata del cálculo y pago de regalías de participación —típico en Media
                &amp; Entertainment— para talento, productores o colaboradores según el desempeño del
                contenido. No he trabajado ese dominio de negocio específico en los últimos años, pero
                sí tengo un antecedente directo en Media/Entretenimiento con TV Azteca, y sobre todo
                mucha experiencia con sistemas de cálculo financiero de alta sensibilidad: aportaciones,
                amortizaciones, distribuciones y liquidaciones masivas para fondos de ahorro y
                pensiones con cerca de un millón de usuarios. La lógica de negocio —reglas de cálculo,
                distribución, trazabilidad— es muy similar en estructura, así que la curva de
                aprendizaje del dominio específico sería rápida.
              </p>
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <h2 className={interviewStyles.sectionTitle}>Simulación de preguntas y respuestas</h2>

          <div className={interviewStyles.script}>
            {[
              {
                q: 'Describe tu experiencia dando soporte a aplicaciones en producción.',
                a: 'En New York Life daba soporte directo a una aplicación on-premise integrada con Salesforce. Monitoreaba la salud del sistema, hacía análisis de causa raíz cuando había incidentes, cubría guardias de fin de semana, participaba en ventanas de liberación nocturnas y atendía tickets operativos vía ServiceNow priorizando por SLA. También mejoré el performance de queries en Oracle y reduje la latencia de servicios con Entity Framework, así que el soporte no era solo reactivo, sino que buscaba prevenir el próximo incidente.',
              },
              {
                q: '¿Qué experiencia tienes con Kubernetes específicamente?',
                a: 'Mi experiencia más profunda en contenedores ha sido con Docker sobre AWS ECS y Fargate —empaquetando el runtime de .NET en imágenes Linux— y he trabajado con EKS dentro del stack de AWS. Con Kubernetes puro tengo una base sólida de tres años, aunque no al mismo nivel de profundidad que con .NET o AWS en general. Es un área donde sigo reforzando activamente, y dado que los conceptos de orquestación de contenedores —pods, servicios, despliegues, escalado— son los mismos que ya domino en ECS/Fargate, la transición es directa.',
              },
              {
                q: '¿Has trabajado con SQL Server?',
                a: 'Sí, buena parte de mi trabajo con Entity Framework y ASP.NET a lo largo de los años ha corrido sobre SQL Server, además de PostgreSQL y Oracle en proyectos más recientes. Puedo hablar en detalle de optimización de queries, procedimientos almacenados y tuning de rendimiento en cualquiera de los tres motores.',
              },
              {
                q: 'Este puesto no tiene gente a cargo. ¿Cómo sabemos que no te vas a aburrir o que te vas a ir en unos meses?',
                a: 'Entiendo la preocupación. Lo que busco ahora es un rol donde pueda enfocarme en la ejecución técnica directa —estabilidad, performance, soporte— sin la carga de gestión de personas por un tiempo. No es una posición "de paso" para mí: valoro mucho el trabajo de soporte bien hecho, y en mis roles anteriores, incluso siendo manager, seguía metiéndome a resolver los incidentes más críticos porque es lo que más disfruto. Estoy buscando estabilidad y un stack técnico donde pueda aportar valor inmediato, no un trampolín.',
              },
              {
                q: '¿Cuáles son tus expectativas salariales?',
                a: 'Prefiero primero entender bien el alcance completo del rol y el rango que manejan para la posición, para asegurarnos de que haya un match justo para ambas partes. ¿Podrías compartirme el rango presupuestado? (Nota: mantener la respuesta abierta y devolver la pregunta evita anclarse primero sin información completa del rango de la empresa.)',
              },
              {
                q: 'Cuéntame de una vez que resolviste un incidente crítico en producción.',
                a: 'En New York Life implementamos un sistema de reintentos con Dead Letter Queue para mensajes fallidos en la integración con SQS entre Salesforce y el sistema on-premise. Antes de eso, cualquier falla de mensajería se perdía silenciosamente y generaba inconsistencias de datos que solo se detectaban días después. Diseñé la cola de reintentos, definí las reglas de cuándo un mensaje pasaba a DLQ, agregué alertas para que el equipo se enterara del fallo de inmediato en lugar de días después, y documenté el proceso de reprocesamiento manual para los mensajes atrapados. El resultado fue una integración mucho más resiliente y trazable, y ese mismo patrón lo repliqué después para otros flujos críticos del sistema.',
              },
            ].map((qa, i) => (
              <div key={i} className={interviewStyles.turn}>
                <p className={interviewStyles.speaker} style={{ color: 'var(--brand-accent-dark)' }}>
                  P: {qa.q}
                </p>
                <p className={interviewStyles.en}>{qa.a}</p>
              </div>
            ))}
          </div>
        </div>
      </DashboardLayout.Content>
    </DashboardLayout.Root>
  );
}
